/**
 * 面包屑推导 hook
 * 根据当前路由路径自动生成面包屑导航
 */

import { useLocation } from 'react-router-dom'
import { ROUTES, ROUTE_META, type RouteMeta } from '../constants/routes'

export interface BreadcrumbItem {
  label: string
  path: string | null
}

/**
 * 转义正则表达式中的特殊字符
 */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * 匹配路由模式
 * 将实际路径与路由模式进行匹配
 * 例如：/exam/paper/123 匹配 /exam/paper/:paperId
 */
function matchRoutePattern(pathname: string): string | null {
  // 精确匹配
  if (ROUTE_META[pathname]) {
    return pathname
  }

  // 模式匹配
  const patterns = Object.keys(ROUTE_META)
  for (const pattern of patterns) {
    if (pattern.includes(':')) {
      // 转义正则表达式中的特殊字符，然后替换参数占位符
      const escapedPattern = escapeRegExp(pattern)
      const regex = new RegExp(
        '^' + escapedPattern.replace(/\\:[^/]+/g, '[^/]+') + '$'
      )
      if (regex.test(pathname)) {
        return pattern
      }
    }
  }

  return null
}

/**
 * 获取路由的完整父级链
 * 例如：/exam/paper/:paperId → ['/exam', '/']
 */
function getAncestorChain(pattern: string): string[] {
  const chain: string[] = []
  let current = pattern

  while (current) {
    const meta = ROUTE_META[current]
    if (!meta?.parent) {
      break
    }
    chain.push(meta.parent)
    current = meta.parent
  }

  return chain
}

/**
 * 构建面包屑路径
 * 从根到当前页面的完整路径
 */
function buildBreadcrumbPath(pattern: string): BreadcrumbItem[] {
  const meta = ROUTE_META[pattern]
  if (!meta) {
    return []
  }

  // 获取祖先链（从父到根）
  const ancestors = getAncestorChain(pattern)

  // 构建面包屑项（从根到当前）
  const items: BreadcrumbItem[] = []

  // 添加祖先（从根到父）
  for (let i = ancestors.length - 1; i >= 0; i--) {
    const ancestorMeta = ROUTE_META[ancestors[i]]
    if (ancestorMeta) {
      items.push({
        label: ancestorMeta.label,
        path: ancestors[i], // 可点击
      })
    }
  }

  // 添加当前项（不可点击）
  items.push({
    label: meta.label,
    path: null,
  })

  return items
}

/**
 * useBreadcrumb hook
 * 返回当前页面的面包屑导航数据
 */
export function useBreadcrumb(): BreadcrumbItem[] {
  const location = useLocation()
  const pathname = location.pathname

  // 匹配路由模式
  const pattern = matchRoutePattern(pathname)

  if (!pattern) {
    return []
  }

  // 构建面包屑路径
  return buildBreadcrumbPath(pattern)
}

/**
 * 获取路由标签
 */
export function getRouteLabel(pathname: string): string {
  const pattern = matchRoutePattern(pathname)
  if (pattern) {
    return ROUTE_META[pattern]?.label || ''
  }
  return ''
}

/**
 * 检查路径是否匹配
 */
export function isPathActive(currentPath: string, targetPath: string): boolean {
  if (currentPath === targetPath) {
    return true
  }

  // 检查是否是子路径
  if (targetPath !== '/' && currentPath.startsWith(targetPath)) {
    return true
  }

  return false
}
